'use client';

import { useState } from 'react';
import { Check } from 'lucide-react';
import { TrackedCTA } from '@/components/analytics/TrackedCTA';
import type { CtaLocation } from '@/lib/analytics/ym';
import { SITE } from '@/lib/site-config';
import { FadeUp, Stagger, StaggerItem } from '@/components/motion';

type Pkg = {
  name: string;
  price: string;
  term: string;
  bullets: string[];
  audience: string;
  featured?: boolean;
  cta: CtaLocation;
};

const packages: Pkg[] = [
  {
    name: 'Разведка',
    price: 'от 50 000 RUB',
    term: '1-2 недели',
    bullets: [
      'Аудит одного процесса',
      'Дорожная карта на 1-2 пилота',
      'Оценка данных и инфраструктуры',
      'Письменный отчёт с приоритетами',
    ],
    audience: 'не понимаете, с чего начать, и не хотите тратить бюджет пилота на гипотезу.',
    cta: 'services_recon',
  },
  {
    name: 'Пилот',
    price: 'от 150 000 RUB',
    term: '1-2 месяца',
    bullets: [
      'Один процесс под ключ в проде',
      'Стек на выбор: RAG / CV / AI-слой над Bitrix24 / 1С',
      'Интеграция с вашей CRM или 1С',
      'Измеряемая метрика на выходе',
    ],
    audience: 'есть процесс и боль, нужно работающее решение, а не презентация.',
    featured: true,
    cta: 'services_pilot',
  },
  {
    name: 'Трансформация',
    price: 'от 400 000 RUB',
    term: '3-6 месяцев',
    bullets: [
      'Автоматизация направления (несколько процессов)',
      'Архитектура с расчётом на масштаб',
      'Поэтапная сдача с актами',
      'Передача в эксплуатацию команде клиента',
    ],
    audience: 'пилот сработал, нужно раскатать на всё направление.',
    cta: 'services_transform',
  },
];

export function Services() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section
      id="services"
      className="bg-background container mx-auto max-w-6xl px-4 md:px-6 lg:px-8 py-24 md:py-32"
    >
      <FadeUp>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Пакеты {SITE.brand}
        </p>
      </FadeUp>
      <FadeUp delay={0.1}>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
          3 формата работы — выбирайте по зрелости задачи
        </h2>
      </FadeUp>

      <Stagger className="mt-10 grid gap-6 md:grid-cols-3">
        {packages.map((pkg, i) => {
          const isHovered = hoveredIndex === i;
          const isAnyHovered = hoveredIndex !== null;
          const isActive = isHovered || (pkg.featured && !isAnyHovered);
          const isDimmed = isAnyHovered && !isHovered;

          return (
            <StaggerItem key={pkg.name}>
              <article
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => document.querySelector('#lead')?.scrollIntoView({ behavior: 'smooth' })}
                className={[
                  'relative flex flex-col rounded-xl bg-card p-6 text-card-foreground h-full',
                  'cursor-pointer transition-all duration-300',
                  isHovered
                    ? 'ring-1 ring-primary shadow-xl -translate-y-1'
                    : isActive
                      ? 'ring-1 ring-primary shadow-md'
                      : 'shadow-sm',
                  isDimmed ? 'opacity-50' : 'opacity-100',
                ].join(' ')}
              >
                {pkg.featured && (
                  <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                    Рекомендуемый
                  </span>
                )}

                <h3 className="text-xl font-semibold">{pkg.name}</h3>
                <p className="mt-3 text-2xl font-bold tracking-tight">{pkg.price}</p>
                <p className="mt-1 text-sm text-muted-foreground">{pkg.term}</p>

                <ul className="mt-6 space-y-2">
                  {pkg.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <p className="mt-6 text-sm italic text-muted-foreground">
                  Кому: {pkg.audience}
                </p>

                <div className="mt-auto pt-6">
                  <TrackedCTA
                    location={pkg.cta}
                    href="#lead"
                    variant={isActive ? 'default' : 'outline'}
                    className="w-full min-h-11 rounded-full transition-all duration-300"
                  >
                    Запросить мини-аудит
                  </TrackedCTA>
                </div>
              </article>
            </StaggerItem>
          );
        })}
      </Stagger>
    </section>
  );
}
